import { Link } from 'react-router-dom'
import { Shield, ArrowLeft } from 'lucide-react'

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <Shield className="h-8 w-8 text-indigo-600" />
                            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
                        </div>
                        <Link
                            to="/login"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-200"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Login
                        </Link>
                    </div>
                    <p className="text-gray-600">
                        Last updated: {new Date().toLocaleDateString()}
                    </p>
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100">
                    <div className="prose prose-lg max-w-none">
                        
                        {/* Introduction */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                Welcome to SHAReVision Camera Management System. This Privacy Policy explains how we collect, 
                                use, disclose, and safeguard your information when you use our camera monitoring and management platform.
                            </p>
                            <p className="text-gray-700 leading-relaxed">
                                By using our service, you agree to the collection and use of information in accordance with this policy.
                            </p>
                        </section>

                        {/* Information We Collect */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
                            
                            <h3 className="text-xl font-medium text-gray-800 mb-3">Personal Information</h3>
                            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                                <li>Email address and contact information</li>
                                <li>Name and organizational details</li>
                                <li>Authentication credentials (encrypted)</li>
                                <li>User preferences and settings</li>
                            </ul>

                            <h3 className="text-xl font-medium text-gray-800 mb-3">Technical Information</h3>
                            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                                <li>Camera feeds and monitoring data</li>
                                <li>System usage logs and analytics</li>
                                <li>Device information and IP addresses</li>
                                <li>Session data and access timestamps</li>
                            </ul>
                        </section>

                        {/* How We Use Information */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
                            <ul className="list-disc list-inside text-gray-700 space-y-2">
                                <li>Provide and maintain camera monitoring services</li>
                                <li>Authenticate users and ensure secure access</li>
                                <li>Monitor system performance and troubleshoot issues</li>
                                <li>Send important notifications and security alerts</li>
                                <li>Improve our services and user experience</li>
                                <li>Comply with legal obligations and security requirements</li>
                            </ul>
                        </section>

                        {/* Data Security */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                We implement robust security measures to protect your personal information and camera data:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 space-y-2">
                                <li>End-to-end encryption for all data transmission</li>
                                <li>Secure authentication with OTP verification</li>
                                <li>Regular security audits and vulnerability assessments</li>
                                <li>Access controls and role-based permissions</li>
                                <li>Secure data storage with backup and recovery systems</li>
                            </ul>
                        </section>

                        {/* Information Sharing */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 space-y-2">
                                <li>With your explicit consent</li>
                                <li>To comply with legal obligations or court orders</li>
                                <li>To protect our rights, property, or safety</li>
                                <li>With trusted service providers under strict confidentiality agreements</li>
                                <li>In case of business transfer or merger (with prior notice)</li>
                            </ul>
                        </section>

                        {/* Data Retention */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
                            <p className="text-gray-700 leading-relaxed">
                                We retain your personal information only for as long as necessary to provide our services and comply with legal obligations. 
                                Camera footage and monitoring data are typically retained for 30-90 days unless longer retention is required by law or requested by you.
                            </p>
                        </section>

                        {/* Your Rights */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
                            <ul className="list-disc list-inside text-gray-700 space-y-2">
                                <li>Access your personal information</li>
                                <li>Correct inaccurate or incomplete data</li>
                                <li>Delete your account and associated data</li>
                                <li>Export your data in a portable format</li>
                                <li>Opt-out of non-essential communications</li>
                                <li>File complaints with relevant data protection authorities</li>
                            </ul>
                        </section>

                        {/* Cookies and Tracking */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking</h2>
                            <p className="text-gray-700 leading-relaxed">
                                We use essential cookies to maintain your session and provide core functionality. 
                                We may also use analytical cookies to improve our services. You can manage cookie preferences through your browser settings.
                            </p>
                        </section>

                        {/* Changes to Policy */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
                            <p className="text-gray-700 leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                                Privacy Policy on this page and updating the "Last updated" date. Continued use of our services after changes 
                                constitutes acceptance of the updated policy.
                            </p>
                        </section>

                        {/* Contact Information */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                            <p className="text-gray-700 leading-relaxed">
                                If you have any questions about this Privacy Policy or our data practices, please contact us through our support channels.
                            </p>
                        </section>

                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-8">
                    <Link
                        to="/login"
                        className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg transition-all duration-200 shadow-md"
                    >
                        <ArrowLeft className="h-5 w-5 mr-2" />
                        Return to Login
                    </Link>
                </div>
            </div>
        </div>
    )
}

export default PrivacyPolicy